import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar as CalIcon, Clock, CheckCircle2, Loader2, User, Phone, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionTag } from "./Reveal";
import { fetchBookingSlots, createBooking } from "@/lib/api";

const AR_DAYS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const AR_MONTHS = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];

const nextDays = (n = 14) => {
  const arr = [];
  const today = new Date();
  for (let i = 1; i <= n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    arr.push(d);
  }
  return arr;
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingCalendar() {
  const { t, lang } = useLang();
  const days = useMemo(() => nextDays(14), []);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", topic: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedTime("");
    fetchBookingSlots(iso(selectedDate))
      .then((r) => setSlots(r.data.open ? r.data.slots : []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) { toast.error(t.booking.errPickSlot); return; }
    if (!form.name.trim() || form.phone.trim().length < 7) { toast.error(t.booking.errFields); return; }
    setSending(true);
    try {
      const r = await createBooking({
        ...form,
        date: iso(selectedDate),
        time: selectedTime,
        lang,
      });
      setConfirmed(r.data);
      toast.success(t.booking.success);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error(t.booking.errTaken);
      else if (status === 429) toast.error(t.booking.errRate);
      else toast.error(t.booking.errGeneric);
    } finally { setSending(false); }
  };

  const dayLabel = (d) => (lang === "ar" ? AR_DAYS[d.getDay()] : d.toLocaleDateString(lang === "ku" ? "ku" : "en", { weekday: "short" }));
  const dateLabel = (d) => (lang === "ar" ? `${d.getDate()} ${AR_MONTHS[d.getMonth()]}` : d.toLocaleDateString(lang === "ku" ? "ku" : "en", { month: "short", day: "numeric" }));

  return (
    <section id="booking" className="relative section-pad" data-testid="booking-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 60%, rgba(0,242,96,0.06), transparent 55%)" }} />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center mb-12">
          <Reveal><SectionTag>{t.booking.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 flex items-center justify-center gap-3">
              <CalIcon className="text-primary" size={36} /> {t.booking.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{t.booking.subtitle}</p>
          </Reveal>
        </div>

        {confirmed ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-surface rounded-3xl p-10 text-center max-w-2xl mx-auto"
            data-testid="booking-confirmed"
          >
            <div className="h-20 w-20 rounded-full mx-auto grid place-items-center mb-5" style={{ background: "rgba(0,242,96,0.15)" }}>
              <CheckCircle2 size={40} className="text-primary" />
            </div>
            <h3 className="text-2xl font-extrabold mb-3">{t.booking.confirmedTitle}</h3>
            <p className="text-slate-400 mb-5">{t.booking.confirmedSub}</p>
            <div className="inline-flex flex-wrap justify-center gap-3 text-sm">
              <span className="rounded-full px-4 py-2 border border-white/15 font-bold">
                <CalIcon size={14} className="inline me-1.5 -mt-0.5" /> {dateLabel(selectedDate)}
              </span>
              <span className="rounded-full px-4 py-2 border border-white/15 font-bold" dir="ltr">
                <Clock size={14} className="inline me-1.5 -mt-0.5" /> {confirmed.time}
              </span>
            </div>
          </motion.div>
        ) : (
          <div className="card-surface rounded-3xl p-6 sm:p-8">
            {/* Date strip */}
            <div className="mb-6">
              <label className="block text-sm font-bold mb-3">{t.booking.pickDate}</label>
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2 snap-x">
                {days.map((d) => {
                  const isSelected = selectedDate && iso(selectedDate) === iso(d);
                  const isFri = d.getDay() === 5;
                  return (
                    <button
                      key={iso(d)}
                      type="button"
                      onClick={() => !isFri && setSelectedDate(d)}
                      disabled={isFri}
                      className={`snap-start shrink-0 w-24 h-24 rounded-2xl border transition-all flex flex-col items-center justify-center gap-1 ${isSelected ? "text-[#04120a] border-transparent" : isFri ? "border-white/5 opacity-40 cursor-not-allowed" : "border-white/10 hover:border-[color:var(--primary)]"}`}
                      style={isSelected ? { background: "var(--primary)" } : {}}
                      data-testid={`booking-day-${iso(d)}`}
                    >
                      <span className="text-xs opacity-80">{dayLabel(d)}</span>
                      <span className="text-lg font-extrabold">{d.getDate()}</span>
                      <span className="text-[10px] opacity-70">{lang === "ar" ? AR_MONTHS[d.getMonth()].slice(0, 3) : d.toLocaleDateString("en", { month: "short" })}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedDate && (
              <div className="mb-6">
                <label className="block text-sm font-bold mb-3">{t.booking.pickTime}</label>
                {loadingSlots ? (
                  <div className="flex justify-center py-6"><Loader2 className="animate-spin text-primary" /></div>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-slate-500">{t.booking.closedDay}</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
                    {slots.map((s) => (
                      <button
                        key={s.time}
                        type="button"
                        disabled={!s.available}
                        onClick={() => setSelectedTime(s.time)}
                        className={`rounded-xl py-2.5 text-sm font-bold border transition-colors ${selectedTime === s.time ? "text-[#04120a] border-transparent" : !s.available ? "border-white/5 opacity-40 line-through cursor-not-allowed" : "border-white/10 hover:border-[color:var(--primary)]"}`}
                        style={selectedTime === s.time ? { background: "var(--primary)" } : {}}
                        dir="ltr"
                        data-testid={`booking-slot-${s.time}`}
                      >
                        {s.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedTime && (
              <form onSubmit={submit} className="pt-6 border-t border-white/10 grid sm:grid-cols-2 gap-3">
                <div className="relative">
                  <User size={16} className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-500 pointer-events-none" />
                  <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.contact.fields.name} className="w-full rounded-xl bg-[#0d1836] border border-white/10 ps-11 pe-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="booking-name" />
                </div>
                <div className="relative">
                  <Phone size={16} className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-500 pointer-events-none" />
                  <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.contact.fields.phone} dir="ltr" className="w-full rounded-xl bg-[#0d1836] border border-white/10 ps-11 pe-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="booking-phone" />
                </div>
                <div className="relative">
                  <Mail size={16} className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-500 pointer-events-none" />
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.contact.fields.email} dir="ltr" className="w-full rounded-xl bg-[#0d1836] border border-white/10 ps-11 pe-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="booking-email" />
                </div>
                <input type="text" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder={t.booking.topicPh} className="rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="booking-topic" />
                <div className="sm:col-span-2 relative">
                  <MessageSquare size={16} className="absolute top-3.5 start-4 text-slate-500 pointer-events-none" />
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t.booking.notesPh} rows={3} className="w-full rounded-xl bg-[#0d1836] border border-white/10 ps-11 pe-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)] resize-y" data-testid="booking-notes" />
                </div>
                <button type="submit" disabled={sending} className="sm:col-span-2 rounded-full px-6 py-3 text-sm font-bold text-[#04120a] disabled:opacity-60 inline-flex items-center justify-center gap-2" style={{ background: "var(--primary)" }} data-testid="booking-submit">
                  {sending ? <><Loader2 size={16} className="animate-spin" /> {t.booking.sending}</> : <><CalIcon size={16} /> {t.booking.submit}</>}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
